import React from 'react';
import { Tag, Button, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getPeriodFromTimestamp } from '@/utils/durationCalculator';
import { APPROVAL_STATUS_MAP } from './index';

/**
 * 下载文件
 * @param mediaId 文件的 media_id
 * @param fileName 文件名（可选）
 */
const downloadFile = async (mediaId: string, fileName?: string) => {
  try {
    const response = await fetch(`/api/v1/wechat/media/download?media_id=${mediaId}`);

    // 检查响应类型
    const contentType = response.headers.get('content-type');

    // 如果是 JSON，说明出错了
    if (contentType && contentType.includes('application/json')) {
      const errorData = await response.json();
      message.error(`下载失败：${errorData.errmsg || '未知错误'}`);
      return;
    }

    // 获取文件流
    const blob = await response.blob();

    // 尝试从响应头获取文件名
    const contentDisposition = response.headers.get('content-disposition');
    let downloadFileName = fileName || 'download';

    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (fileNameMatch && fileNameMatch[1]) {
        downloadFileName = fileNameMatch[1].replace(/['"]/g, '');
        // 处理 URL 编码的文件名
        try {
          downloadFileName = decodeURIComponent(downloadFileName);
        } catch (e) {
          // 如果解码失败，使用原始文件名
        }
      }
    }

    // 创建下载链接
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = downloadFileName;
    document.body.appendChild(link);
    link.click();

    // 清理
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    message.success('文件下载成功');
  } catch (error) {
    console.error('下载文件失败:', error);
    message.error('下载文件失败，请稍后重试');
  }
};

/**
 * 根据控件类型渲染申请内容的值
 */
export const renderControlValue = (content: any) => {
  const controlType = content.control;
  const value = content.value;

  if (!value) {
    return <span style={{ color: '#999' }}>无数据</span>;
  }

  switch (controlType) {
    // 文本类
    case 'Text':
    case 'Textarea':
      return <div>{value.text || '-'}</div>;

    // 数字类
    case 'Number':
      return <div>{value.new_number || '-'}</div>;

    // 金额
    case 'Money':
      return <div style={{ color: '#f5222d', fontWeight: 'bold' }}>¥{value.new_money || '0.00'}</div>;

    // 日期
    case 'Date':
      if (value.date?.s_timestamp) {
        return <div>{dayjs.unix(value.date.s_timestamp).format('YYYY-MM-DD')}</div>;
      }
      return <div>-</div>;

    // 日期范围
    case 'DateRange':
      const dr = value?.date_range;
      if (dr) {
        const startTime = dayjs.unix(dr.new_begin);
        const endTime = dayjs.unix(dr.new_end);
        const duration = dr.new_duration;
        // 根据企业微信文档，halfday类型每半天为43200秒（12小时）
        const halfDays = Math.round(duration / 43200);

        // 判断上午还是下午：使用getPeriodFromTimestamp函数
        const startPeriod = getPeriodFromTimestamp(dr.new_begin);
        const endPeriod = getPeriodFromTimestamp(dr.new_end);

        return (
          <div>
            <div>开始：{startTime.format('YYYY-MM-DD')} {startPeriod}</div>
            <div>结束：{endTime.format('YYYY-MM-DD')} {endPeriod}</div>
            <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
              时长：{halfDays * 0.5}天
            </div>
          </div>
        );
      }
      return <div>-</div>;

    // 选择器
    case 'Selector':
      if (value.options && Array.isArray(value.options)) {
        return (
          <div>
            {value.options.map((option: any, idx: number) => (
              <Tag key={idx} color="blue" style={{ marginRight: 4, marginBottom: 4 }}>
                {option.value?.find((v: any) => v.lang === 'zh_CN')?.text || option.key}
              </Tag>
            ))}
          </div>
        );
      }
      if (value.selector?.options && Array.isArray(value.selector?.options)) {
        return (
          <div>
            {value.selector?.options.map((option: any, idx: number) => (
              <Tag key={idx} color="blue" style={{ marginRight: 4, marginBottom: 4 }}>
                {option.value?.find((v: any) => v.lang === 'zh_CN')?.text || option.key}
              </Tag>
            ))}
          </div>
        );
      }
      return <div>-</div>;

    // 成员
    case 'Contact':
      if (value.members && Array.isArray(value.members) && value.members.length > 0) {
        return (
          <div>
            {value.members.map((member: OA.ApprovalMember, idx: number) => (
              <Tag key={idx} color="green" style={{ marginRight: 4, marginBottom: 4 }}>
                {member.username || member.name || member.userid}
              </Tag>
            ))}
          </div>
        );
      }
      return <div>-</div>;

    // 文件
    case 'File':
      if (value.files && Array.isArray(value.files)) {
        return (
          <div>
            {value.files.map((file: any, idx: number) => (
              <div key={idx} style={{ marginBottom: 8 }}>
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadFile(file.file_id, `文件${idx + 1}`)}
                  style={{ padding: 0 }}
                >
                  {file.file_name || `文件 ${idx + 1}`}
                </Button>
              </div>
            ))}
          </div>
        );
      }
      return <div>-</div>;

    // 位置
    case 'Location':
      if (value.location) {
        return (
          <div>
            <div><strong>{value.location.title || '位置'}</strong></div>
            <div style={{ color: '#666', fontSize: 12 }}>{value.location.address || '-'}</div>
            <div style={{ color: '#999', fontSize: 11 }}>
              经纬度：{value.location.latitude}, {value.location.longitude}
            </div>
          </div>
        );
      }
      return <div>-</div>;

    // 明细（Table控件，数据在 value.children 中）
    case 'Table':
      if (value.children && Array.isArray(value.children) && value.children.length > 0) {
        return (
          <div>
            {value.children.map((row: any, rowIdx: number) => (
              <div
                key={rowIdx}
                style={{
                  padding: '8px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  backgroundColor: '#fafafa',
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>明细 {rowIdx + 1}</div>
                {row.list && Array.isArray(row.list) && row.list.map((item: any, itemIdx: number) => {
                  const label = item.title?.find((t: any) => t.lang === 'zh_CN')?.text || item.id;
                  let displayValue = '-';
                  if (item.value) {
                    if (item.control === 'Text' || item.control === 'Textarea') {
                      displayValue = item.value.text || '-';
                    } else if (item.control === 'Number') {
                      displayValue = item.value.new_number || '-';
                    } else if (item.control === 'Money') {
                      displayValue = item.value.new_money ? `¥${item.value.new_money}` : '-';
                    } else if (item.value.text) {
                      displayValue = item.value.text;
                    } else {
                      displayValue = item.value.new_number || item.value.new_money || '-';
                    }
                  }
                  return (
                    <div key={itemIdx} style={{ fontSize: 12, marginLeft: 8, marginBottom: 2 }}>
                      <span style={{ color: '#666' }}>{label}：</span>
                      <span>{displayValue}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        );
      }
      return <div>-</div>;

    // 假期
    case 'Vacation':
      if (value.vacation) {
        const vacationType = value.vacation.selector?.options?.[0]?.key;
        const dateRange = value.vacation.attendance?.date_range;
        if (dateRange) {
          const startTime = dayjs.unix(dateRange.new_begin);
          const endTime = dayjs.unix(dateRange.new_end);
          const duration = dateRange.new_duration;
          // 根据企业微信文档，halfday类型每半天为43200秒（12小时）
          const halfDays = Math.round(duration / 43200);

          // 判断上午还是下午：使用getPeriodFromTimestamp函数
          const startPeriod = getPeriodFromTimestamp(dateRange.new_begin);
          const endPeriod = getPeriodFromTimestamp(dateRange.new_end);

          return (
            <div>
              {vacationType && (
                <div style={{ marginBottom: 4 }}>
                  <strong>假期类型：</strong>
                  <Tag color="blue">{vacationType}</Tag>
                </div>
              )}
              <div>开始：{startTime.format('YYYY-MM-DD')} {startPeriod}</div>
              <div>结束：{endTime.format('YYYY-MM-DD')} {endPeriod}</div>
              <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
                时长：{halfDays * 0.5}天
              </div>
            </div>
          );
        }
      }
      return <div>-</div>;

    // 考勤（时间范围，半天单位）
    case 'Attendance':
      let thisValue = value;
      if ('attendance' in thisValue) {
        thisValue = thisValue.attendance;
      }
      const dateRange = thisValue?.date_range;
      if (dateRange) {
        const startTime = dayjs.unix(dateRange.new_begin);
        const endTime = dayjs.unix(dateRange.new_end);
        const duration = dateRange.new_duration;
        // 根据企业微信文档，halfday类型每半天为43200秒（12小时）
        const halfDays = Math.round(duration / 43200);

        // 判断上午还是下午：使用getPeriodFromTimestamp函数
        const startPeriod = getPeriodFromTimestamp(dateRange.new_begin);
        const endPeriod = getPeriodFromTimestamp(dateRange.new_end);

        return (
          <div>
            <div>开始：{startTime.format('YYYY-MM-DD')} {startPeriod}</div>
            <div>结束：{endTime.format('YYYY-MM-DD')} {endPeriod}</div>
            <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
              时长：{halfDays * 0.5}天
            </div>
          </div>
        );
      }
      return <div>-</div>;

    // 电话号码
    case 'PhoneNumber':
      if (value.phonenumber) {
        return <div>{value.phonenumber.area} {value.phonenumber.number}</div>;
      }
      return <div>-</div>;
    case 'Tips':
      return <div></div>;

    // 关联审批
    case 'RelatedApproval':
      if (value.related_approval && Array.isArray(value.related_approval)) {
        return (
          <div>
            {value.related_approval.map((approval: any, idx: number) => (
              <div
                key={idx}
                style={{
                  padding: '8px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  backgroundColor: '#fafafa',
                }}
              >
                <div style={{ marginBottom: 4 }}>
                  <strong>单号：</strong>
                  <Tag color="blue" style={{ marginLeft: 4 }}>{approval.sp_no}</Tag>
                </div>
                {approval.template_names && approval.template_names.length > 0 && (
                  <div style={{ marginBottom: 4 }}>
                    <strong>模板：</strong>
                    {approval.template_names.find((t: any) => t.lang === 'zh_CN')?.text ||
                     approval.template_names[0]?.text || '-'}
                  </div>
                )}
                {approval.name && (
                  <div style={{ marginBottom: 4 }}>
                    <strong>申请人：</strong>
                    {approval.name}
                  </div>
                )}
                {approval.sp_status && (
                  <div style={{ marginBottom: 4 }}>
                    <strong>状态：</strong>
                    <Tag color={APPROVAL_STATUS_MAP[approval.sp_status as OA.ApprovalStatus]?.color}>
                      {APPROVAL_STATUS_MAP[approval.sp_status as OA.ApprovalStatus]?.text || '未知'}
                    </Tag>
                  </div>
                )}
                {approval.create_time && (
                  <div style={{ fontSize: 12, color: '#999' }}>
                    创建时间：{dayjs.unix(approval.create_time).format('YYYY-MM-DD HH:mm:ss')}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      }
      return <div>-</div>;

    // 默认处理：显示JSON
    default:
      return (
        <div>
          <Tag color="orange" style={{ marginBottom: 4 }}>
            未知控件类型: {controlType}
          </Tag>
          <pre style={{ fontSize: 11, color: '#666' }}>
            {JSON.stringify(value, null, 2)}
          </pre>
        </div>
      );
  }
};

